import { Menu, Transition } from "@headlessui/react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LogOut, User as UserIcon } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { cn } from "@/lib/cn";
import { ROLE_LABEL } from "@/lib/roles";
import { getTeamSettings } from "@/services/team";

function initialsOf(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? "")
    .join("");
}

export function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  // Team name reads from the same ["team", "settings"] key the Organization
  // page writes to, so a rename in Settings propagates live via realtime
  // (useRealtimeSync invalidates ["team", "settings"] on teams UPDATE).
  const teamSettingsQuery = useQuery({
    queryKey: ["team", "settings", user?.teamId ?? null],
    queryFn: () => (user?.teamId ? getTeamSettings(user.teamId) : Promise.resolve(null)),
    enabled: !!user?.teamId,
    staleTime: 60_000,
  });

  if (!user) return null;
  const initials = initialsOf(user.fullName);

  return (
    <div className="flex items-center gap-2">
      <Menu as="div" className="relative">
        <Menu.Button className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-50 text-sm font-semibold text-brand-700 ring-1 ring-brand-100 hover:ring-brand-200 focus:outline-none focus:ring-2 focus:ring-brand-500">
          {initials || <UserIcon className="h-4 w-4" />}
        </Menu.Button>
        <Transition
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right divide-y divide-slate-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
            <div className="px-4 py-3">
              {teamSettingsQuery.data?.name && (
                <p className="truncate text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {teamSettingsQuery.data.name}
                </p>
              )}
              <p className="text-sm font-medium text-slate-900">{user.fullName}</p>
              <p className="truncate text-xs text-slate-500">{user.email}</p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-brand-600">
                {ROLE_LABEL[user.role]}
              </p>
            </div>
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/settings/profile"
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 text-sm",
                      active ? "bg-slate-50 text-slate-900" : "text-slate-700",
                    )}
                  >
                    <UserIcon className="h-4 w-4" />
                    Account
                  </Link>
                )}
              </Menu.Item>
            </div>
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => void signOut()}
                    className={cn(
                      "flex w-full items-center gap-2 px-4 py-2 text-sm",
                      active ? "bg-slate-50 text-rose-700" : "text-rose-600",
                    )}
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
}
